/**
 * ResetPassword — public page consumed via emailed reset link.
 *
 * Flow:
 *   1. Super admin triggers `POST /api/super-admin/users/{id}/send-reset-email`
 *   2. User receives Resend email with link `${FRONTEND_URL}/reset-password?token=...`
 *   3. User lands here, enters new password, submits to
 *      `POST /api/auth/reset-password` (public, rate-limited)
 *   4. On success → redirect to /login with toast
 */
import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState('form'); // form | success | error
    const [message, setMessage] = useState('');

    if (!token) {
        return (
            <PageShell>
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    No reset token
                </h2>
                <p className="text-stone-500 text-sm mb-6">
                    This page only works when opened via the link in your reset email.
                </p>
                <Link to="/login" className="inline-block bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors">
                    Back to Login
                </Link>
            </PageShell>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password || password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        if (password !== confirm) {
            toast.error('Passwords do not match');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/api/auth/reset-password`, {
                token,
                new_password: password,
            });
            setStatus('success');
            setMessage(res.data?.message || 'Password updated. You can now sign in.');
            toast.success('Password updated');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to reset password';
            setStatus('error');
            setMessage(detail);
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    if (status === 'success') {
        return (
            <PageShell>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    Password Updated
                </h2>
                <p className="text-stone-500 text-sm mb-6">{message}</p>
                <Link to="/login" className="inline-block bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors">
                    Sign In
                </Link>
            </PageShell>
        );
    }

    if (status === 'error') {
        return (
            <PageShell>
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    Reset Failed
                </h2>
                <p className="text-stone-500 text-sm mb-6">{message}</p>
                <Link to="/login" className="inline-block bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors">
                    Back to Login
                </Link>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <h2 className="text-xl font-semibold text-stone-900 mb-2 text-center" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                Set a new password
            </h2>
            <p className="text-stone-500 text-sm mb-6 text-center">
                Enter a new password below. The reset link is valid for 24 hours from when it was sent.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">New password</label>
                    <div className="relative">
                        <input
                            type={show ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            className="w-full px-3 py-2.5 pr-10 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                            autoFocus
                            required
                            minLength={8}
                        />
                        <button
                            type="button"
                            onClick={() => setShow(s => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
                            tabIndex={-1}
                        >
                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Confirm password</label>
                    <input
                        type={show ? 'text' : 'password'}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Re-enter your new password"
                        className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                        required
                        minLength={8}
                    />
                </div>
                <button
                    type="submit"
                    disabled={submitting || !password || !confirm}
                    className="w-full bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submitting ? 'Updating…' : 'Update password'}
                </button>
            </form>

            <p className="text-center text-xs text-stone-400 mt-6">
                Need help? Contact your platform admin.
            </p>
        </PageShell>
    );
}

function PageShell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                </div>
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 text-center">
                    {children}
                </div>
            </div>
        </div>
    );
}
